/**
 * The recurrence form's state, and the conversions either side of it.
 *
 * Pure, and deliberately not inside the component file: this is the logic that
 * decides what dates a series will land on, so it has to be testable without
 * mounting React.
 */

import { DAY_NAMES, ORDINAL_NAMES, joinOrdinals } from "convex/recurrenceLabel";

export interface RecurrenceConfig {
  period: "weekly" | "monthly";
  interval: number;                              // 1–12: repeat every N weeks/months
  monthlyType: "day_of_month" | "nth_weekday";  // monthly sub-option
  ordinals: number[];                            // which weeks of the month, e.g. [2, 4]
  firstEventDiffers: boolean;                   // whether the first event has a special date
  firstEventDate: string;                        // ISO date of the exception first event
  endDate: string;                               // ISO date or empty string
}

/** The weeks of the month a monthly series can land on. */
export const ORDINAL_CHOICES = [1, 2, 3, 4];

export function ordinalLabel(n: number): string {
  return ORDINAL_NAMES[n] ?? `${n}th`;
}

/** "1st", "2nd & 4th", "1st, 2nd & 4th" */
export const ordinalListLabel = joinOrdinals;

export function deriveFromDate(dateStr: string) {
  const d = new Date(dateStr + "T00:00:00");
  const day = d.getUTCDate();
  const dayOfWeek = d.getUTCDay();
  // Weeks are counted from the start of the month, capped at the 4th. Nobody
  // schedules by "last Friday" in social dance, and a 5th-week start date is
  // better read as the 4th than given a rule of its own.
  const ordinal = Math.min(Math.ceil(day / 7), 4);
  return { dayOfWeek, dayOfMonth: day, ordinal };
}

export function defaultRecurrenceConfig(startDate: string): RecurrenceConfig {
  return {
    period: "weekly",
    interval: 1,
    monthlyType: "day_of_month",
    ordinals: [deriveFromDate(startDate).ordinal],
    firstEventDiffers: false,
    firstEventDate: startDate,
    endDate: "",
  };
}

/**
 * A stored pattern read back into form state, so an existing series' schedule
 * can be edited rather than only set once at creation.
 *
 * The legacy single `ordinal` folds into the list, and a `-1` (last weekday)
 * becomes the 4th — the selector no longer offers "last", and a series stored
 * that way is one whose organizer meant the 4th in the first place.
 */
export function fromRecurrenceArgs(
  pattern: {
    frequency: string;
    interval?: number;
    ordinal?: number;
    ordinals?: number[];
    dayOfMonth?: number;
    endDate?: string;
  },
  startDate: string
): RecurrenceConfig {
  const base = defaultRecurrenceConfig(startDate);
  const stored = pattern.ordinals?.length
    ? pattern.ordinals
    : pattern.ordinal !== undefined
      ? [pattern.ordinal]
      : [];
  const ordinals = stored.map((o) => (o === -1 ? 4 : o)).filter((o) => o >= 1 && o <= 4);

  const weekly = pattern.frequency === "weekly" || pattern.frequency === "biweekly";

  return {
    ...base,
    period: weekly ? "weekly" : "monthly",
    interval: pattern.frequency === "biweekly" ? 2 : (pattern.interval ?? 1),
    monthlyType: pattern.frequency === "monthly" ? "day_of_month" : "nth_weekday",
    ordinals: ordinals.length > 0 ? ordinals : base.ordinals,
    endDate: pattern.endDate ?? "",
  };
}

/**
 * The recurrence arguments `createRecurringEvent` expects, worked out from the
 * form's config and the event's own date. Shared so the submit form and the
 * edit modal cannot drift apart on what a config means.
 */
export function toRecurrenceArgs(config: RecurrenceConfig, date: string) {
  const { dayOfWeek, dayOfMonth } = deriveFromDate(date);

  if (config.period === "weekly") {
    return {
      frequency: "weekly" as const,
      dayOfWeek,
      dayOfMonth: undefined,
      ordinals: undefined,
    };
  }
  if (config.monthlyType === "day_of_month") {
    return {
      frequency: "monthly" as const,
      dayOfWeek: undefined,
      dayOfMonth,
      ordinals: undefined,
    };
  }
  return {
    frequency: "nth_weekday_of_month" as const,
    dayOfWeek,
    dayOfMonth: undefined,
    // An empty selection would generate nothing at all, so fall back to the
    // week the start date itself falls in.
    ordinals: config.ordinals.length > 0 ? config.ordinals : [deriveFromDate(date).ordinal],
  };
}

